import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { contactApi } from '../services/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    message: '',
    website: '' // Honeypot field - should remain empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formStartTime] = useState(Date.now()); // Track when form was loaded

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear validation error for this field when user starts typing
    if (validationErrors[e.target.name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setValidationErrors({});

    // Spam Protection: Honeypot check
    if (formData.website) {
      // Bot filled the honeypot field
      console.log('Spam detected: honeypot field filled');
      setSubmitting(false);
      // Pretend it succeeded to fool the bot
      setSubmitted(true);
      return;
    }

    // Spam Protection: Time-based check (form should take at least 3 seconds to fill)
    const timeTaken = Date.now() - formStartTime;
    if (timeTaken < 3000) {
      console.log('Spam detected: form submitted too quickly');
      setSubmitting(false);
      // Pretend it succeeded to fool the bot
      setSubmitted(true);
      return;
    }

    try {
      // Remove honeypot field before sending
      const { website, ...submitData } = formData;
      await contactApi.submit(submitData);

      setSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          businessName: '',
          email: '',
          phone: '',
          message: '',
          website: ''
        });
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      // Check if it's a validation error (400)
      if (err.response?.status === 400 && err.response?.data?.errors) {
        // Extract validation errors
        const errors = {};
        Object.keys(err.response.data.errors).forEach(key => {
          errors[key.toLowerCase()] = err.response.data.errors[key][0];
        });
        setValidationErrors(errors);
        setError('Please fix the errors below');
      } else {
        const errorMessage = err.response?.data?.message || 'Failed to send message. Please try emailing us directly at support@suddyflow.com';
        setError(errorMessage);
      }
      console.error('Contact form error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section with Background */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        {/* Background Image - REPLACE: Search "customer support" or "communication" */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-800/70 to-blue-900/80 z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-lg">
            Get in Touch
          </h1>
          <p className="text-xl max-w-3xl mx-auto drop-shadow-md">
            Have questions about SuddyFlow? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>
              
              {submitted && (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6">
                  <div className="flex items-center">
                    <div className="text-green-500 text-3xl mr-4">✓</div>
                    <div>
                      <h3 className="font-bold text-green-900 mb-1">Message Sent!</h3>
                      <p className="text-green-700">We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6 mb-6">
                  <div className="flex items-center">
                    <div className="text-red-500 text-3xl mr-4">⚠</div>
                    <div>
                      <h3 className="font-bold text-red-900 mb-1">Error</h3>
                      <p className="text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from humans, bots will fill it */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  autoComplete="off"
                  tabIndex="-1"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                  aria-hidden="true"
                />

                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    disabled={submitting}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none disabled:bg-gray-100 ${
                      validationErrors.name ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder="John Smith"
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="businessName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Name
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    disabled={submitting}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none disabled:bg-gray-100 ${
                      validationErrors.businessname ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder="ABC Landscaping"
                  />
                  {validationErrors.businessname && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.businessname}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={submitting}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none disabled:bg-gray-100 ${
                      validationErrors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder="john@abclandscaping.com"
                  />
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={submitting}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none disabled:bg-gray-100 ${
                      validationErrors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder="(555) 123-4567"
                  />
                  {validationErrors.phone && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message * <span className="text-xs text-gray-500">(at least 10 characters)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    disabled={submitting}
                    rows="5"
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none disabled:bg-gray-100 ${
                      validationErrors.message ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder="Tell us about your business and how we can help..."
                  ></textarea>
                  {validationErrors.message && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Information & Quick Links */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Contact Information</h2>
              
              {/* Email Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-lg mb-6 border-2 border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-white text-2xl">📧</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Email</h3>
                    <a href="mailto:support@suddyflow.com" className="text-blue-600 hover:text-blue-700 font-medium">
                      support@suddyflow.com
                    </a>
                  </div>
                </div>
                <p className="text-gray-700 text-sm">
                  We typically respond within 24 hours during business days.
                </p>
              </div>

              {/* Response Time Card */}
              <div className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-lg mb-6 border-2 border-green-200">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-white text-2xl">⏱️</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Response Time</h3>
                    <p className="text-green-700 font-medium">Within 24 Hours</p>
                  </div>
                </div>
                <p className="text-gray-700 text-sm">
                  We're committed to responding to all inquiries quickly and thoroughly.
                </p>
              </div>

              {/* Quick Links */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg border-2 border-purple-200">
                <h3 className="text-xl font-bold mb-4">Quick Links</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/features" className="text-blue-600 hover:text-blue-700 font-medium flex items-center">
                      <span className="mr-2">→</span> View All Features
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-blue-600 hover:text-blue-700 font-medium flex items-center">
                      <span className="mr-2">→</span> See Pricing
                    </Link>
                  </li>
                  <li>
                    <Link to="/about" className="text-blue-600 hover:text-blue-700 font-medium flex items-center">
                      <span className="mr-2">→</span> Read Our Story
                    </Link>
                  </li>
                  <li>
                    <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium flex items-center">
                      <span className="mr-2">→</span> Start Free Trial
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-500 transition-all">
              <h3 className="text-xl font-bold mb-3">How long is the free trial?</h3>
              <p className="text-gray-600">
                You get 14 days completely free, no credit card required. Cancel anytime during 
                the trial with no charges.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-500 transition-all">
              <h3 className="text-xl font-bold mb-3">Can I switch plans later?</h3>
              <p className="text-gray-600">
                Absolutely! You can upgrade or downgrade your plan at any time. Changes take 
                effect immediately, and we'll prorate any charges.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-500 transition-all">
              <h3 className="text-xl font-bold mb-3">Is my data secure?</h3>
              <p className="text-gray-600">
                Yes. We use bank-level encryption for all data. Your customer information, 
                financial data, and business details are completely secure with enterprise-grade protection.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-500 transition-all">
              <h3 className="text-xl font-bold mb-3">Do you offer training or support?</h3>
              <p className="text-gray-600">
                Yes! We provide onboarding assistance, tutorial videos, and responsive email 
                support. We're here to help you succeed.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-500 transition-all">
              <h3 className="text-xl font-bold mb-3">Can I import my existing customer data?</h3>
              <p className="text-gray-600">
                Yes. You can import customers, jobs, and other data from spreadsheets or export 
                files from other systems. We'll help you get set up.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-500 transition-all">
              <h3 className="text-xl font-bold mb-3">What if I need help getting started?</h3>
              <p className="text-gray-600">
                Just reach out! We're a small team that cares about your success. We'll personally 
                help you get set up and answer any questions you have.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image - REPLACE: Search "handshake" or "business agreement" */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-blue-900/80 z-0" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-6 drop-shadow-lg">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-blue-100 drop-shadow-md">
            Don't wait—start your free trial today and see how SuddyFlow can 
            transform your business.
          </p>
          <Link 
            to="/register" 
            className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 shadow-lg transition-colors"
          >
            Start Free 14-Day Trial
          </Link>
          <p className="mt-4 text-blue-200">
            No credit card required • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;